import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Recently Viewed Products section component.
 * Reads product IDs from localStorage and fetches rendered product cards
 * via the Section Rendering API using Shopify's search endpoint.
 */
class RecentlyViewedProducts extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    const viewedProducts = this.#getFilteredProducts();

    if (viewedProducts.length === 0) {
      // No recently viewed products — hide the entire section
      this.style.display = 'none';
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Gets recently viewed product IDs, excluding the current product if on a product page.
   * @returns {string[]}
   */
  #getFilteredProducts() {
    const viewedProducts = RecentlyViewed.getProducts();
    const currentProductId = this.dataset.currentProductId;

    if (currentProductId) {
      return viewedProducts.filter((id) => id !== currentProductId);
    }

    return viewedProducts;
  }

  /**
   * Loads recently viewed products by fetching rendered HTML
   * via the Section Rendering API with a search URL.
   */
  async #loadProducts() {
    if (this.#loaded) return;
    this.#loaded = true;

    const viewedProducts = this.#getFilteredProducts();
    if (viewedProducts.length === 0) {
      this.style.display = 'none';
      return;
    }

    const { sectionId, searchUrl } = this.dataset;
    if (!sectionId || !searchUrl) return;

    // Build the search URL with product IDs
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);

      if (this.#activeFetch?.signal.aborted) return;

      if (!html) {
        this.style.display = 'none';
        return;
      }

      // Parse the response and check if products were found
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newSection = parsed.getElementById(`recently-viewed-products-${sectionId}`);

      if (!newSection || !newSection.innerHTML.trim()) {
        this.style.display = 'none';
        return;
      }

      morph(this, newSection);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Recently viewed products error:', error.message);
        this.style.display = 'none';
      }
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
